/**
 * English strings. Error copy is keyed by the API's error `code`, so components never branch on message
 * text. Add a language by adding a sibling file and registering it in `shared/config/i18n.ts`.
 */
export const en = {
  errors: {
    INVALID_CREDENTIALS: "Incorrect email or password.",
    INVALID_REFRESH: "Your session has expired. Please sign in again.",
    UNAUTHENTICATED: "Please sign in again.",
    FORBIDDEN: "You don't have permission to do that.",
    NOT_FOUND: "That item no longer exists or is no longer shared with you.",
    NAME_EXISTS: "An item with that name already exists here.",
    CONTENT_MISMATCH: "That file isn't a valid file of its type.",
    UNSAFE_XML: "This XML uses entities that aren't allowed.",
    WRONG_BUCKET: "XML files go in an XML folder and PDFs in a PDF folder.",
    PAYLOAD_TOO_LARGE: "That file is too large.",
    FILE_TOO_LARGE_TO_PREVIEW: "Too large to preview. Download it instead.",
    RATE_LIMITED: "Too many requests. Please wait a moment.",
    ACCOUNT_LOCKED: "This account is temporarily locked.",
    WEAK_PASSWORD: "Choose a stronger password.",
    STORAGE_ERROR: "Storage is unavailable right now. Try again shortly.",
    DUPLICATE_FILE: "An identical file has already been uploaded.",
    EMPTY_FILE: "This file is empty.",
    UNSUPPORTED_MEDIA_TYPE: "Only .xml and .pdf files can be uploaded.",
    PART_SIZE_MISMATCH:
      "The upload was interrupted in a way that could not be recovered. Try again.",
    INCOMPLETE_UPLOAD: "Some parts did not arrive. Try again.",
    UPLOAD_EXPIRED: "The upload expired. Start it again.",
    TOO_MANY_UPLOADS: "Too many uploads at once. Waiting for a free slot.",
    NETWORK_ERROR: "Network problem. Retry to continue where it stopped.",
    INTERNAL_ERROR: "Something went wrong on our side.",
    UNKNOWN: "Something went wrong.",
    withRef: "{{message}} (ref {{requestId}})",
  },
  common: {
    appName: "Files",
    cancel: "Cancel",
    close: "Close",
    previous: "Previous",
    next: "Next",
    copy: "Copy",
    reload: "Reload",
    loading: "Loading",
    pagination: "Pagination",
    pageRange: "{{first}}–{{last}} of {{total}}",
  },
  dashboard: {
    folders: "Folders",
    files: "Files",
    storage: "Storage used",
    recentFiles: "Recent files",
    yourFolders: "Your folders",
    sharedWithYou: "Shared with you",
    openMyFiles: "Open My files",
    openShared: "Open Shared with me",
    viewAllCount: "View all ({{count}})",
    empty:
      "Nothing here yet. Upload files or a whole folder from My files, or ask someone to share a folder with you.",
    goToFiles: "Go to My files",
  },
  nav: {
    main: "Main",
    dashboard: "Dashboard",
    myFiles: "My files",
    shared: "Shared with me",
    trash: "Trash",
    admin: "Admin",
    users: "Users",
    audit: "Audit log",
    openMenu: "Open menu",
    menu: "Menu",
    account: "Account menu",
    security: "Security",
    signOut: "Sign out",
    searchPlaceholder: "Search files and folders",
    themeLight: "Switch to light theme",
    themeDark: "Switch to dark theme",
  },
  auth: {
    email: "Email",
    password: "Password",
    signIn: "Sign in",
    signingIn: "Signing in…",
    or: "or",
    signInZoho: "Sign in with Zoho",
    zoho_state: "The Zoho sign-in expired or was interrupted. Try again.",
    zoho_denied: "Zoho sign-in was cancelled.",
    zoho_failed: "Zoho could not complete the sign-in. Try again.",
    zoho_no_account: "There is no account for that Zoho email. Ask an admin to add you.",
    enterEmail: "Enter your email",
    enterPassword: "Enter your password",
    lockedFor_one: "This account is temporarily locked. Try again in {{count}} minute.",
    lockedFor_other: "This account is temporarily locked. Try again in {{count}} minutes.",
    lockedLater: "This account is temporarily locked. Try again later.",
    tooManyAttempts: "Too many attempts. Try again in {{seconds}} seconds.",
    serverUnreachable: "Could not reach the server. Reload to try again.",
    currentPassword: "Current password",
    newPassword: "New password",
    repeatPassword: "Repeat new password",
    changePassword: "Change password",
    saving: "Saving…",
    passwordsDiffer: "Passwords do not match",
    currentIncorrect: "Current password is incorrect",
    passwordChanged: "Password changed",
    otherSessionsOut: "Your other sessions were signed out.",
    securityTitle: "Security",
    securityHint: "Changing your password signs you out of all your other sessions.",
  },
  shortcuts: {
    title: "Keyboard shortcuts",
    hint: "Press ? any time to open this list.",
    rows: [
      ["↑ ↓ ← →", "Move between items"],
      ["Shift + arrows", "Extend the selection"],
      ["Ctrl/⌘ + A", "Select everything loaded"],
      ["Space", "Select or unselect the current item"],
      ["Enter", "Open the folder or preview the file"],
      ["F2", "Rename"],
      ["M", "Move to…"],
      ["Delete", "Move to trash (you can undo)"],
      ["Shift + F10, Menu key", "Open the actions menu"],
      ["Esc", "Clear the selection or close a dialog"],
      ["← →", "Previous/next file while previewing"],
    ],
  },
  pages: {
    notFoundTitle: "Page not found",
    notFoundBody: "It may have moved, or it is no longer shared with you.",
    goToFiles: "Go to your files",
    noAccessTitle: "No access",
    noAccessBody: "You don't have permission to view this page.",
    errorTitle: "Something went wrong",
    errorBody: "Try reloading the page. If it keeps happening, contact support.",
    requestId: "Request id",
    requestIdCopied: "Request id copied",
  },
  drive: {
    folderNotFound: "Folder not found",
    folderNotFoundBody: "It may have been deleted, or it is no longer shared with you.",
    backToFiles: "Back to your files",
    filesAndFolders: "Files and folders",
    emptyFolder: "This folder is empty",
    emptyFolderHint: "Create a folder or drop files here.",
    emptyFolderReadOnly: "This folder is empty.",
    breadcrumb: "Breadcrumb",
    switchToGrid: "Switch to grid view",
    switchToList: "Switch to list view",
    sortBy: "Sort by",
    sort: "Sort",
    sortNameAsc: "Name (A–Z)",
    sortNameDesc: "Name (Z–A)",
    sortDateDesc: "Newest first",
    sortDateAsc: "Oldest first",
    sortSizeDesc: "Largest first",
    sortSizeAsc: "Smallest first",
    colName: "Name",
    colOwner: "Owner",
    colModified: "Modified",
    colSize: "Size",
    actionsForSelected: "Actions for selected items",
    actions: "Actions",
    loadingMore: "Loading more…",
    movingItems_one: "Moving {{count}} item",
    movingItems_other: "Moving {{count}} items",
    itemCount_one: "{{count}} item",
    itemCount_other: "{{count}} items",
  },
  actions: {
    open: "Open",
    download: "Download",
    downloadFolder: "Download as .zip",
    rename: "Rename",
    move: "Move",
    moveTo: "Move to…",
    delete: "Delete",
    share: "Share…",
    newFolder: "New folder",
    folderName: "Folder name",
    create: "Create",
    save: "Save",
    name: "Name",
    renameItem: "Rename {{type}}",
    selectedCount: "{{count}} selected",
    clearSelection: "Clear selection",
    selectionActions: "Selection actions",
    moveTitle_one: "Move 1 item",
    moveTitle_other: "Move {{count}} items",
    moveDescription: "Choose a folder you can edit.",
    destinationFolder: "Destination folder",
    currentLocation: "current location",
    topLevel: "{{bucket}} (top level)",
    noFoldersToMoveInto: "No folders you can move into.",
    moveHere: "Move here",
    deletedToast: "Deleted",
    movedToast: "Moved",
    restoredToast: "Restored",
    couldNotDelete: "Could not delete",
    couldNotMove: "Could not move",
    couldNotUndo: "Could not undo",
    undo: "Undo",
  },
  upload: {
    upload: "Upload",
    uploadFiles: "Upload files…",
    uploadFolder: "Upload folder…",
    chooseFiles: "Choose files to upload",
    chooseFolder: "Choose a folder to upload",
    dropToUpload: "Drop to upload",
    uploads: "Uploads",
    uploadedSummary: "{{done}} of {{total}} uploaded",
    pausedSummary: "{{paused}} paused",
    pausedWithDoneSummary: "{{paused}} paused, {{done}} uploaded",
    failedSummary: "{{done}} uploaded, {{failed}} failed",
    completeSummary_one: "{{done}} upload complete",
    completeSummary_other: "{{done}} uploads complete",
    expandUploads: "Expand uploads",
    collapseUploads: "Collapse uploads",
    clearCompleted: "Clear completed",
    cancelAll: "Cancel all",
    confirmCancelAll: "Cancel all uploads?",
    retryAll: "Retry all",
    pauseAll: "Pause all",
    resumeAll: "Resume all",
    moreRows_one: "{{count}} more file not shown",
    moreRows_other: "{{count}} more files not shown",
    statusWaiting: "Waiting",
    statusPreparing: "Preparing…",
    statusUploading: "Uploading",
    statusFinishing: "Finishing… (checking the file)",
    statusUploaded: "Uploaded",
    statusExisting: "Already uploaded",
    statusFailed: "Failed",
    statusCancelled: "Cancelled",
    statusPaused: "Paused",
    retry: "Retry",
    pause: "Pause",
    resume: "Resume",
    cancel: "Cancel",
    pickReplacement: "Select file to resume upload",
  },
  trash: {
    trash: "Trash",
    trashEmpty: "Trash is empty",
    itemsInTrash_one: "{{count}} item in the trash",
    itemsInTrash_other: "{{count}} items in the trash",
    emptyTrash: "Empty trash",
    emptyDescription: "Nothing here. Deleted items you can restore appear in this list.",
    topLevel: "Top level",
    inFolder: "in {{folder}}",
    deletedAt: "deleted {{date}}",
    restore: "Restore",
    deleteForever: "Delete forever",
    loadMore: "Load more",
    deleteForeverTitle: "Delete forever?",
    deleteForeverDesc: "“{{name}}”{{extra}} will be permanently deleted. This cannot be undone.",
    andEverythingInIt: " and everything in it",
    emptyTrashTitle: "Empty trash?",
    emptyTrashDesc:
      "Everything you own in the trash will be permanently deleted. Items owned by someone else stay in the trash. This cannot be undone.",
    nameInUseTitle: "Name already in use",
    nameInUseDesc:
      "Something with this name now exists in the original location. Choose another name to restore it.",
    restoreAs: "Restore as",
    typeWordToConfirm: "Type {{word}} to confirm",
    restoredToast: "Restored “{{name}}”",
    restoreParentFirst: "Restore “{{name}}” first",
    restoreParentFirstDesc: "It is still in the trash.",
    couldNotRestore: "Could not restore",
    couldNotDeleteForever: "Could not delete forever",
    couldNotEmpty: "Could not empty the trash",
  },
  preview: {
    file: "File",
    previewDesc:
      "File preview. Use the left and right arrow keys to move between files, Escape to close.",
    previousFile: "Previous file",
    nextFile: "Next file",
    openInNewTab: "Open in new tab",
    download: "Download",
    closePreview: "Close preview",
    loadingPreview: "Loading preview",
    couldNotLoad: "Could not load the preview",
    noLongerAvailable: "No longer available",
    noLongerAvailableDesc: "This file was deleted or is no longer shared with you.",
    tooLargeTitle: "Too large to preview",
    tooLargeDesc: "Download the file instead.",
    noPreviewTypeTitle: "No preview for this file type",
    noPreviewTypeDesc: "Download the file instead.",
    tryAgain: "Try again",
    previewCrashed: "The preview could not be shown.",
  },
  adminUsers: {
    users: "Users",
    addUser: "New user",
    filterRole: "Filter by role",
    allRoles: "All roles",
    roleAdmin: "Admin",
    roleUser: "User",
    searchPlaceholder: "Search users by email",
    colEmail: "Email",
    colRole: "Role",
    colStatus: "Status",
    colCreated: "Created",
    colActions: "Actions",
    statusActive: "Active",
    statusLocked: "Locked",
    statusDeactivated: "Deactivated",
    makeAdmin: "Make admin",
    makeUser: "Make regular user",
    deactivate: "Deactivate",
    reactivate: "Reactivate",
    resetPassword: "Reset password",
    resetPasswordAction: "Reset password…",
    unlock: "Unlock account",
    makeAdminTitle: "Make admin?",
    makeAdminText: "{{email}} will be able to manage users and see the audit log.",
    makeUserTitle: "Remove admin rights?",
    makeUserText: "{{email}} will no longer be able to manage users.",
    deactivateTitle: "Deactivate user?",
    deactivateText: "{{email}} will be signed out and unable to sign in. Their files stay.",
    createUserTitle: "Create user",
    createUserDesc: "Add a new user to the system.",
    temporaryPassword: "Temporary password",
    passwordCopied: "Password copied",
    couldNotCopy: "Could not copy the password",
    generate: "Generate",
    userCreated: "User created",
    userUpdated: "User updated",
    passwordReset: "Password reset",
    accountUnlocked: "Account unlocked",
  },
  adminAudit: {
    auditLog: "Audit log",
    exportCsv: "Export CSV",
    filterByUser: "Filter by user",
    allUsers: "All users",
    filterByAction: "Filter by action",
    filterByTargetType: "Filter by target type",
    allTargetTypes: "All target types",
    fromDate: "From date",
    toDate: "To date",
    targetId: "Target ID",
    targetIdPlaceholder: "Filter by target ID",
    colTimestamp: "Timestamp",
    colActor: "Actor",
    colAction: "Action",
    colTarget: "Target",
    colDetails: "Details",
    noLogsFound: "No audit logs found matching the filters.",
  },
  sharing: {
    shareTitle: "Share",
    shareDesc: "Invite people to view or edit this item.",
    emailLabel: "Email address",
    roleViewer: "Viewer",
    roleEditor: "Editor",
    roleOwner: "Owner",
    invite: "Invite",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    accessList: "Who has access",
    removeAccess: "Remove access",
    inherited: "Inherited",
    direct: "Direct",
  },
  search: {
    resultsFor: "Results for “{{query}}”",
    search: "Search",
    type: "Type",
    all: "All",
    searchResults: "Search results",
    open: "Open",
    showInFolder: "Show in folder",
    noResults: "No results for “{{query}}”.",
    prompt: "Type in the search box to find files and folders you can access.",
  },
  shared: {
    sharedWithMe: "Shared with me",
    all: "All",
    filterByType: "Filter by type",
    itemsSharedWithYou: "Items shared with you",
    empty: "Nothing has been shared with you yet.",
  },
} as const;

export type Resources = typeof en;
